import { useEffect, useReducer } from "react";
import { get } from "lodash";
import {
  ApolloInjection,
  PaymentSource,
  SupportedPaymentMethod,
  PaymentSourceType,
  PaymentSourceInput,
} from "@zapier/payment/esm/types";
import { mapErrorMessage } from "@zapier/payment/esm/utils";
import usePaymentSources from "./usePaymentSources";
import useAttemptTracking, {
  attemptEvents,
  attemptNodes,
} from "./useAttemptTracking";

type Input = {
  accountId: number | undefined;
  onSaveSuccess: (() => void) | undefined;
  onTrack: (
    b: string,
    a: {
      attemptId: string;
      attemptPath: string;
    }
  ) => void;
  startEdit: boolean;
} & ApolloInjection;

type Output = {
  currentSource: PaymentSource | undefined | null;
  error: string | undefined | null;
  forceCardUpdate: boolean;
  isEditing: boolean;
  isLoading: boolean;
  isSaving: boolean;
  method: PaymentSourceType | null;
  onCancelEdit: () => void;
  onEdit: (a: PaymentSourceType | null) => void;
  onError: (error: Error, method: PaymentSourceType | null) => void;
  onHideSuccessMessage: () => void;
  onSave: (b: PaymentSourceInput) => void;
  onSelectMethod: (b: PaymentSourceType, a: boolean) => void;
  showSuccessMessage: boolean;
  supportedMethods: Array<SupportedPaymentMethod>;
};

type State = {
  error: string | undefined | null;
  isEditing: boolean;
  method: PaymentSourceType | null;
  showSuccessMessage: boolean;
};

const actions = {
  CANCEL_EDIT: "CANCEL_EDIT",
  EDIT_DETAILS: "EDIT_DETAILS",
  HIDE_SUCCESS_MESSAGE: "HIDE_SUCCESS_MESSAGE",
  SAVE_DETAILS: "SAVE_DETAILS",
  SAVE_DETAILS_DONE: "SAVE_DETAILS_DONE",
  SAVE_DETAILS_FAIL: "SAVE_DETAILS_FAIL",
  SELECT_METHOD: "SELECT_METHOD",
  SET_METHOD: "SET_METHOD",
} as const;

type Action =
  | {
      type: "CANCEL_EDIT" | "SELECT_METHOD" | "SET_METHOD";
      method: PaymentSourceType | null;
    }
  | { type: "SAVE_DETAILS_FAIL"; message: string }
  | { type: "EDIT_DETAILS" }
  | { type: "HIDE_SUCCESS_MESSAGE" }
  | { type: "SAVE_DETAILS" }
  | { type: "SAVE_DETAILS_DONE" };

const initialState = {
  error: null,
  isEditing: false,
  method: null,
  showSuccessMessage: false,
};

const reducer = (state: State, action: Action) => {
  switch (action.type) {
    case actions.CANCEL_EDIT:
      return { ...initialState, method: action.method };

    case actions.EDIT_DETAILS:
      return { ...state, isEditing: true, showSuccessMessage: false };

    case actions.HIDE_SUCCESS_MESSAGE:
      return { ...state, showSuccessMessage: false };

    case actions.SAVE_DETAILS:
      return { ...state, error: null };

    case actions.SAVE_DETAILS_DONE:
      return {
        ...state,
        error: null,
        isEditing: false,
        showSuccessMessage: true,
      };

    case actions.SAVE_DETAILS_FAIL:
      return { ...state, error: action.message };

    case actions.SELECT_METHOD:
      return {
        error: null,
        isEditing: true,
        method: action.method,
        showSuccessMessage: false,
      };

    case actions.SET_METHOD:
      return { ...state, method: action.method };

    default:
      throw new Error();
  }
};

const getPaymentMethod = (
  savedSource: PaymentSource | undefined | null,
  method?: PaymentSourceType | null
) => {
  if (method) return method;
  return savedSource?.method ?? null;
};

const usePaymentSourceState = ({
  accountId,
  onSaveSuccess,
  onTrack,
  startEdit,
  ...apolloInjection
}: Input): Output => {
  const [state, dispatch] = useReducer(reducer, {
    ...initialState,
    isEditing: startEdit,
  });
  const { resetAttempt, trackAttempt } = useAttemptTracking({
    onTrack,
  });

  const onError = (error: Error, method: PaymentSourceType | null) => {
    // Only dispatch error if the current payment method hasn't changed since the error was thrown
    if (state.method === method) {
      const message = mapErrorMessage(error);
      trackAttempt({
        event: attemptEvents.error,
        path: attemptNodes.error,
      });
      dispatch({
        message,
        type: actions.SAVE_DETAILS_FAIL,
      });
    }
  };

  const onSuccess = ({
    saveBankPaymentSource,
  }: {
    saveBankPaymentSource: PaymentSource;
  }) => {
    dispatch({
      type: actions.SAVE_DETAILS_DONE,
    }); // fire off any side-effects in app

    if (onSaveSuccess) {
      onSaveSuccess();
    }

    trackAttempt({
      event: attemptEvents.save,
      path: attemptNodes.save,
      // @ts-ignore - trackAttempt does not expect country...but it's here?
      country:
        get(saveBankPaymentSource, ["details", "address_country"]) ||
        get(saveBankPaymentSource, ["addressCountry"]),
      type:
        get(saveBankPaymentSource, ["type"]) ||
        get(saveBankPaymentSource, ["method"]),
    });
    resetAttempt();
  };

  const paymentSources = usePaymentSources({
    accountId,
    method: state.method,
    onError,
    onSuccess,
    ...apolloInjection,
  }); // Selects the primary payment source method (if exists) when it
  // first loads

  useEffect(() => {
    if (!state.method && paymentSources.primarySource) {
      dispatch({
        method: getPaymentMethod(paymentSources.primarySource),
        type: actions.SET_METHOD,
      });
    } // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [paymentSources.primarySource]);

  const onSelectMethod = (method: PaymentSourceType, hasSwitched: boolean) => {
    dispatch({
      method: getPaymentMethod(paymentSources.primarySource, method),
      type: actions.SELECT_METHOD,
    });

    const nodeType = (hasSwitched ? `switch-${method}` : `select-${method}`) as
      | "switch-paypal"
      | "switch-bank"
      | "switch-card"
      | "select-paypal"
      | "select-bank"
      | "select-card";

    trackAttempt({
      event: attemptEvents.switch,
      path: attemptNodes[nodeType],
    });
  };

  const onEdit = (method: PaymentSourceType | null) => {
    dispatch({
      type: actions.EDIT_DETAILS,
    });
    trackAttempt({
      data: {
        isNew: false,
        method,
      },
      event: attemptEvents.start,
      path: attemptNodes.edit,
    });
  };

  const onSave = (details: PaymentSourceInput) => {
    dispatch({
      type: actions.SAVE_DETAILS,
    });
    paymentSources.onSave(details, state.method);
  };

  const onCancelEdit = () => {
    trackAttempt({
      event: attemptEvents.cancel,
      path: attemptNodes.cancel,
    });
    resetAttempt();
    dispatch({
      method: getPaymentMethod(paymentSources.primarySource),
      type: actions.CANCEL_EDIT,
    });
  };

  const onHideSuccessMessage = () => {
    dispatch({
      type: actions.HIDE_SUCCESS_MESSAGE,
    });
  };

  const { primarySource } = paymentSources; // used to force users to update their CC details if they do not
  // have a country saved

  const forceCardUpdate = Boolean(
    primarySource &&
      primarySource.kind === PaymentSourceType.card &&
      !primarySource.details?.addressCountry
  );
  return {
    currentSource: paymentSources.primarySource,
    error: state.error,
    forceCardUpdate,
    isEditing: state.isEditing,
    isLoading: paymentSources.isLoading,
    isSaving: paymentSources.isSaving,
    method: state.method,
    onCancelEdit,
    onEdit,
    onError,
    onHideSuccessMessage,
    onSave,
    onSelectMethod,
    showSuccessMessage: state.showSuccessMessage,
    supportedMethods: paymentSources.supportedPaymentMethods,
  };
};

export default usePaymentSourceState;
